import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { useGetCredentialSharingConfiguration } from "@src/appV2/Facilities/api/useGetCredentialSharingConfiguration";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { UpdateChartingCredentialsContainer } from "@src/appV2/redesign/Charting/UpdateChartingCredentialsContainer";
import { YourChartingLoginBottomSheetContainer } from "@src/appV2/redesign/Charting/YourChartingLoginBottomSheetContainer";
import { useCreateChatChannel } from "@src/appV2/ShiftBlocks/api/useCreateChatChannel";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useGetChartingCredentials } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { generatePath, useHistory } from "react-router-dom";

import { ChartingRequiredCardContent } from "./ChartingRequiredCardContent";

export interface ChartingRequiredCardProps {
  shift: Pick<Shift, "_id" | "facility">;
  workerId: string;
}

export function ChartingRequiredCard(props: ChartingRequiredCardProps) {
  const { shift, workerId } = props;

  const saveLoginModalState = useModalState();
  const yourLoginModalState = useModalState();
  const history = useHistory();

  const { mutateAsync: createChatChannel } = useCreateChatChannel();

  const { credentialSharingConfiguration } = useGetCredentialSharingConfiguration(
    shift.facility.userId ?? "",
    {
      enabled: isDefined(shift.facility.userId),
    }
  );

  const { data: chartingCredentialsResponse } = useGetChartingCredentials(
    workerId,
    shift.facility.userId ?? "",
    {
      enabled: isDefined(shift.facility.userId),
    }
  );

  const chartingCredentials =
    chartingCredentialsResponse?.data?.[0]?.attributes?.chartingCredentials;

  const handleRequestLogin = async () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_REQUEST_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });

    try {
      await createChatChannel({
        facilityId: shift.facility.userId ?? "",
        agentId: workerId,
      });

      history.push(
        generatePath(DeprecatedGlobalAppV1Paths.CHAT, {
          facilityId: shift.facility.userId ?? "",
        })
      );
    } catch (error) {
      logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_CHAT_CHANNEL_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        workerId,
        workplaceId: shift.facility.userId ?? "",
        shiftId: shift._id,
      });
    }
  };

  const handleSaveLogin = () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_SAVE_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });
    saveLoginModalState.openModal();
  };

  const handleMyChartingLogin = () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_MY_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });
    yourLoginModalState.openModal();
  };

  // Don't render if feature is not enabled
  if (!credentialSharingConfiguration?.chartingReminders?.bookedShiftDetails.enabled) {
    return null;
  }

  return (
    <>
      <ChartingRequiredCardContent
        chartingCredentials={chartingCredentials}
        onRequestLogin={handleRequestLogin}
        onSaveLogin={handleSaveLogin}
        onMyChartingLogin={handleMyChartingLogin}
      />

      <UpdateChartingCredentialsContainer
        modalState={saveLoginModalState}
        shiftId={shift._id}
        workplaceId={shift.facility.userId ?? ""}
        workerId={workerId}
        chartingCredentials={chartingCredentials}
        onClose={() => {
          saveLoginModalState.closeModal();
        }}
      />

      <YourChartingLoginBottomSheetContainer
        modalState={yourLoginModalState}
        shiftId={shift._id}
        workplaceId={shift.facility.userId ?? ""}
        workerId={workerId}
        chartingCredentials={chartingCredentials}
      />
    </>
  );
}
